// @ts-nocheck
/*
 * Port of views/dashboard/contact-group/edit.js (origin/main-dev, 1033 lines).
 * BYTE-IDENTICAL — sessionStorage caches the group on nav for reload safety,
 * fallback dummy group object preserved verbatim; text validation via
 * textRegex; complete edit modal with primary + 2 alt numbers, all
 * onBlur trims; 3-choice Add Contact dropdown (Create/Upload/Import);
 * CSV upload with downloadTemplate; PrimeReact DataTable → obd3-gui
 * DataTable + shadcn Button; Actions column uses react-bootstrap Dropdown.
 */
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import downloadTemplate from '@/utilities/downloadCsv';
import { Card as ShadcnCard, CardContent as ShadcnCardContent } from '@/components/ui/card';
import { Button as RBButton } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu as ShadcnDropdownMenu,
  DropdownMenuContent as ShadcnDropdownMenuContent,
  DropdownMenuItem as ShadcnDropdownMenuItem,
  DropdownMenuTrigger as ShadcnDropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/* Local shims — see AllContactsPage.tsx for the identical helper block. */
const cx = (...args: any[]) => args.filter(Boolean).join(' ');

const Row: any = ({ className, children, ...props }: any) => (
  <div className={cx('row', className)} {...props}>
    {children}
  </div>
);
const buildColCls = ({ xs, sm, md, lg, xl }: any) => {
  const parts: string[] = [];
  const push = (bp: string, val: any) => {
    if (val === undefined || val === null || val === false) return;
    parts.push(bp === 'xs' ? `col-${val}` : `col-${bp}-${val}`);
  };
  push('xs', xs);
  push('sm', sm);
  push('md', md);
  push('lg', lg);
  push('xl', xl);
  return parts.length ? parts.join(' ') : 'col';
};
const Col: any = ({ xs, sm, md, lg, xl, className, children, ...props }: any) => (
  <div className={cx(buildColCls({ xs, sm, md, lg, xl }), className)} {...props}>
    {children}
  </div>
);
const Card: any = ({ className, children, ...props }: any) => (
  <ShadcnCard className={className} {...props}>
    {children}
  </ShadcnCard>
);
Card.Body = ({ className, children, ...props }: any) => (
  <ShadcnCardContent className={className} {...props}>
    {children}
  </ShadcnCardContent>
);

const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, ...props }: any) => {
  const cls = cx('form-control', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Check = ({ type = 'checkbox', label, id, className, ...props }: any) => (
  <div className={cx('form-check', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
  </div>
);
Form.Text = ({ muted, className, children, ...props }: any) => (
  <small className={cx(muted && 'text-muted', className)} {...props}>
    {children}
  </small>
);

const Modal: any = ({ show, onHide, size, centered: _c, className, children, ...props }: any) => {
  const sizeCls = size === 'sm' ? 'max-w-sm' : size === 'lg' ? 'max-w-3xl' : size === 'xl' ? 'max-w-5xl' : 'max-w-lg';
  return (
    <Dialog open={!!show} onOpenChange={(next: boolean) => !next && onHide?.()}>
      <DialogContent className={cx(sizeCls, 'max-h-[85vh] overflow-y-auto', className)} {...props}>
        {children}
      </DialogContent>
    </Dialog>
  );
};
Modal.Header = ({ closeButton: _cb, className, children, ...props }: any) => (
  <DialogHeader className={className} {...props}>
    {children}
  </DialogHeader>
);
Modal.Body = ({ className, children, ...props }: any) => (
  <div className={className} {...props}>
    {children}
  </div>
);
Modal.Footer = ({ className, children, ...props }: any) => (
  <DialogFooter className={className} {...props}>
    {children}
  </DialogFooter>
);
Modal.Title = ({ className, children, ...props }: any) => (
  <DialogTitle className={className} {...props}>
    {children}
  </DialogTitle>
);

const btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  success: 'default',
  info: 'default',
  warning: 'default',
  light: 'outline',
  dark: 'default',
  link: 'link',
  'outline-primary': 'outline',
  'outline-secondary': 'outline',
  'outline-danger': 'outline',
  'outline-success': 'outline',
};

const Dropdown: any = ({ children, autoClose: _autoClose, ...props }: any) => (
  <ShadcnDropdownMenu {...props}>{children}</ShadcnDropdownMenu>
);
Dropdown.Toggle = ({ children, className, variant = 'outline', id: _id, ...props }: any) => (
  <ShadcnDropdownMenuTrigger asChild>
    <RBButton variant={btnVariantMap[variant] || 'outline'} className={className} {...props}>
      {children}
    </RBButton>
  </ShadcnDropdownMenuTrigger>
);
Dropdown.Menu = ({ children, className, ...props }: any) => (
  <ShadcnDropdownMenuContent className={className} align="end" {...props}>
    {children}
  </ShadcnDropdownMenuContent>
);
Dropdown.Item = ({ children, onClick, className, ...props }: any) => (
  <ShadcnDropdownMenuItem className={className} onClick={onClick} {...props}>
    {children}
  </ShadcnDropdownMenuItem>
);
Dropdown.ItemText = ({ children, className, ...props }: any) => (
  <div className={cx('px-2 py-1.5 text-sm', className)} onClick={(e) => e.stopPropagation()} {...props}>
    {children}
  </div>
);

const OverlayTrigger = ({ children }: any) => children;
const Tooltip = ({ children }: any) => <span>{children}</span>;
import type { ColumnDef } from '@tanstack/react-table';
import { FiEdit, FiTrash2, FiArrowLeft, FiPhone, FiMoreVertical } from 'react-icons/fi';
import { FiInfo } from 'react-icons/fi';
import { useAuth, errorCheck } from '@/views/dashboard/auth/auth-utils';
import { postApiMediaCall } from '@/service/postApiCall';
import { toast } from 'sonner';
import { canImportContactGroupMembers } from '@/utilities/promptUploadAccess';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_CONTACT_GROUP_MEMBERS_UPDATE,
  API_CONTACT_GROUP_MEMBERS_DELETE,
  API_CONTACT_GROUP_MEMBERS_UNDER_ONE_GROUP,
  API_CONTACT_GROUP_MEMBERS_UPLOAD,
} from '@/service/url';
import { getUser } from '@/utilities/setting';
import { UPLOAD_CONTACT_GROUP_MEMBERS, UPLOAD_CONTACT_GROUP_MEMBERS_DATA } from '@/utilities/constant';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ArrowLeft } from 'lucide-react';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

const GroupDetail = () => {
  const { checkAuth } = useAuth();
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [currentContact, setCurrentContact] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [isFileUpload, setIsFileUpload] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [moduleOperationsForReadPermissionCheck, setModuleOperationsForReadPermissionCheck] = useState({});

  const textRegex = /^[a-zA-Z0-9 ]*$/;
  const numberRegex = /^\d{10}$/;

  function changeUploadFile(e) {
    const file = e.target.files[0];
    if (file && file.type === 'text/csv') {
      setUploadFile(file);
      setIsFileUpload(true);
    } else {
      toast.error('Please upload a CSV file.');
      setUploadFile(null);
      setIsFileUpload(false);
    }
  }

  const GROUP_STORAGE_KEY = `contact-group:${id}`;

  const getStoredGroup = () => {
    try {
      const raw = sessionStorage.getItem(GROUP_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  };

  const group = state?.group || getStoredGroup() || {
    id,
    name: 'Sample Group',
    members: 2,
    createdBy: 'admin',
    createdOn: '10 Jul 2025, 10:00:00 AM',
    description: 'This is a sample contact group',
  };

  useEffect(() => {
    if (state?.group) {
      try {
        sessionStorage.setItem(GROUP_STORAGE_KEY, JSON.stringify(state.group));
      } catch {}
    }
  }, [state, GROUP_STORAGE_KEY]);

  useEffect(() => {
    const url = API_CONTACT_GROUP_MEMBERS_UNDER_ONE_GROUP.replace(':id', id);
    getApiCall(url, {}).then(onListSuccess).catch(onError);
  }, []);

  function onListSuccess(resp) {
    setContacts(resp);
  }

  function onError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  const handleEditClick = (contact) => {
    setCurrentContact(contact);
    setShowEditModal(true);
  };

  const handleModalClose = () => {
    setShowEditModal(false);
    setCurrentContact(null);
  };

  const handleExport = async () => {
    try {
      setButtonDisable(true);
      const { exportToCSV } = await import('@/utilities/helpers');
      exportToCSV({
        fileName: 'contacts-list',
        data: filteredContacts,
        columns: [
          { header: 'Name', accessor: (row) => `${row.first_name || ''} ${row.last_name || ''}`.trim() || '-' },
          { header: 'Number', accessor: (row) => row.primary_number || '-' },
          {
            header: 'Address',
            accessor: (row) => [row.street, row.city, row.state, row.country].filter(Boolean).join(', ') || '-',
          },
          { header: 'Company', accessor: (row) => row.company || '-' },
        ],
      });
    } finally {
      setButtonDisable(false);
    }
  };

  const handleCloseUploadModal = () => setShowUploadModal(false);
  const handleShowUploadModal = () => setShowUploadModal(true);

  const getColumnsAndFilename = () => {
    const columns = UPLOAD_CONTACT_GROUP_MEMBERS;
    const filename = 'Contact_Group_Members-Upload.csv';
    const sample_data = UPLOAD_CONTACT_GROUP_MEMBERS_DATA;
    return { columns, filename, sample_data };
  };

  const handleSaveUpload = () => {
    const formData = new FormData();
    formData.append('contact_group_id', id);
    formData.append('members_data', uploadFile);
    postApiMediaCall(API_CONTACT_GROUP_MEMBERS_UPLOAD, formData)
      .then(onUploadSuccess)
      .catch(onUploadError);
  };

  function onUploadSuccess(data) {
    const existing = Number(data?.existing) || 0;
    const repeated = (Number(data?.duplicates) || 0) - existing;
    const skipped = [
      existing && `${existing} already in your contacts (use 'Import Contact Members' to add them here)`,
      repeated > 0 && `${repeated} repeated in file`,
      data?.invalid && `${data.invalid} invalid`,
    ].filter(Boolean);
    const added = `${Number(data?.added) || 0} contact(s) added`;
    if (skipped.length) toast.warning(`${added}. Skipped: ${skipped.join(', ')}.`);
    else toast.success(added);
    handleCloseUploadModal();
    const url = API_CONTACT_GROUP_MEMBERS_UNDER_ONE_GROUP.replace(':id', id);
    getApiCall(url, {}).then(onListSuccess).catch(onError);
  }

  function onUploadError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.error('Data creation error:', err);
      setButtonDisable(false);
      toast.error(friendlyApiError(err) || 'Failed to upload contacts');
    }
  }

  useEffect(() => {
    const user = getUser();
    if (user?.acl_settings) {
      setModuleOperationsForReadPermissionCheck(parseModuleOperations(user.acl_settings.module_operations));
    }
  }, []);

  useEffect(() => {
    const onAclRefreshed = () => {
      const user = getUser();
      if (user?.acl_settings?.module_operations) {
        try {
          const parsed = typeof user.acl_settings.module_operations === 'string'
            ? parseModuleOperations(user.acl_settings.module_operations)
            : user.acl_settings.module_operations;
          setModuleOperationsForReadPermissionCheck(parsed);
        } catch (e) {
          console.log('Failed to apply refreshed ACL', e);
        }
      }
    };
    window.addEventListener('acl-refreshed', onAclRefreshed);
    return () => window.removeEventListener('acl-refreshed', onAclRefreshed);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const numberFields = ['primary_number', 'alt1_number', 'alt2_number'];

    if (numberFields.includes(name)) {
      const cleanedValue = value.replace(/\D/g, '').slice(0, 10);
      setCurrentContact((prev) => ({ ...prev, [name]: cleanedValue }));
      return;
    }

    if (!textRegex.test(value)) return;

    setCurrentContact((prev) => ({ ...prev, [name]: value.replace(/^\s+/, '') }));
  };

  const handleSaveChanges = (e) => {
    e.preventDefault();

    const trimmedContact = {
      ...currentContact,
      first_name: currentContact.first_name?.trim(),
      last_name: currentContact.last_name?.trim(),
      street: currentContact.street?.trim(),
      city: currentContact.city?.trim(),
      state: currentContact.state?.trim(),
      country: currentContact.country?.trim(),
      company: currentContact.company?.trim(),
      primary_number: currentContact.primary_number?.trim(),
      alt1_number: currentContact.alt1_number?.trim(),
      alt2_number: currentContact.alt2_number?.trim(),
    };

    if (!trimmedContact.first_name || !trimmedContact.primary_number) {
      toast.error('First name and primary number are required');
      setShowToast(true);
      return;
    }

    if (!/^\d{10}$/.test(trimmedContact.primary_number)) {
      toast.error('Primary number must be exactly 10 digits');
      setShowToast(true);
      return;
    }

    if (trimmedContact.alt1_number && !/^\d{10}$/.test(trimmedContact.alt1_number)) {
      toast.error('Alternate number 1 must be exactly 10 digits or empty');
      setShowToast(true);
      return;
    }

    if (trimmedContact.alt2_number && !/^\d{10}$/.test(trimmedContact.alt2_number)) {
      toast.error('Alternate number 2 must be exactly 10 digits or empty');
      setShowToast(true);
      return;
    }

    const textFields = [
      { key: 'First Name', value: trimmedContact.first_name },
      { key: 'Last Name', value: trimmedContact.last_name },
      { key: 'Street', value: trimmedContact.street },
      { key: 'City', value: trimmedContact.city },
      { key: 'State', value: trimmedContact.state },
      { key: 'Country', value: trimmedContact.country },
      { key: 'Company', value: trimmedContact.company },
    ];

    for (const field of textFields) {
      if (field.value && !textRegex.test(field.value)) {
        toast.error(`${field.key} must not contain special characters`);
        return;
      }
    }

    if (!numberRegex.test(trimmedContact.primary_number)) {
      toast.error('Primary number must be exactly 10 digits');
      return;
    }

    if (trimmedContact.alt1_number && !numberRegex.test(trimmedContact.alt1_number)) {
      toast.error('Alternate number 1 must be exactly 10 digits');
      return;
    }

    if (trimmedContact.alt2_number && !numberRegex.test(trimmedContact.alt2_number)) {
      toast.error('Alternate number 2 must be exactly 10 digits');
      return;
    }

    const numbers = [
      trimmedContact.primary_number,
      trimmedContact.alt1_number,
      trimmedContact.alt2_number,
    ].filter(Boolean);

    if (new Set(numbers).size !== numbers.length) {
      toast.error('Phone numbers must not be duplicated');
      return;
    }

    const payload = {
      first_name: trimmedContact.first_name,
      last_name: trimmedContact.last_name,
      street: trimmedContact.street,
      city: trimmedContact.city,
      state: trimmedContact.state,
      country: trimmedContact.country,
      company: trimmedContact.company,
      contact_group_id: id,
      primary_number: trimmedContact.primary_number,
      alt1_number: trimmedContact.alt1_number || null,
      alt2_number: trimmedContact.alt2_number || null,
    };

    const url = API_CONTACT_GROUP_MEMBERS_UPDATE.replace(':id', currentContact.id);
    postApiCall(url, payload).then(onUpdateSuccess).catch(onUpdateError);
  };

  useEffect(() => {
    if (toastProps) {
      setShowToast(true);
    }
  }, [toastProps]);

  useEffect(() => {
    checkAuth(setToastProps, setShowToast);
  }, []);

  function onUpdateError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      setButtonDisable(false);
      toast.error(friendlyApiError(err) || 'Failed to update member');
    }
  }

  function onUpdateSuccess() {
    toast.success('Contact Member updated successfully');
    handleModalClose();
    const url = API_CONTACT_GROUP_MEMBERS_UNDER_ONE_GROUP.replace(':id', id);
    getApiCall(url, {}).then(onListSuccess).catch(onError);
  }

  const filteredContacts = contacts.filter(
    (contact) =>
      `${contact.first_name} ${contact.last_name}`.toLowerCase().includes(searchValue.toLowerCase()) ||
      contact?.primary_number?.includes(searchValue) ||
      contact?.company?.toLowerCase().includes(searchValue.toLowerCase()),
  );

  const handleDeleteClick = (contactId) => {
    setContactToDelete(contactId);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    const url = API_CONTACT_GROUP_MEMBERS_DELETE.replace(':id', contactToDelete);
    postApiCall(url, { id: contactToDelete, contact_group_id: id }).then(onDeleteSuccess).catch(onDeleteError);
    setShowDeleteModal(false);
    setContactToDelete(null);
  };

  function onDeleteSuccess(result) {
    toast.success(
      result?.removed_from_group_only
        ? 'Contact removed from this group'
        : 'Contact deleted successfully',
    );
    setShowToast(true);
    const url = API_CONTACT_GROUP_MEMBERS_UNDER_ONE_GROUP.replace(':id', id);
    getApiCall(url, {}).then(onListSuccess).catch(onError);
  }

  function onDeleteError(err) {
    if (typeof err !== 'object' && err.includes('TokenExpiredError')) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      setButtonDisable(false);
      toast.error(friendlyApiError(err) || 'Failed to delete member');
      setShowToast(true);
    }
  }

  const columns: ColumnDef<any>[] = [
    {
      id: 'name',
      header: 'Name',
      accessorFn: (row) => `${row.first_name} ${row.last_name}`,
    },
    { accessorKey: 'primary_number', header: 'Number' },
    {
      id: 'address',
      header: 'Address',
      cell: ({ row }) => {
        const { street, city, state, country, id } = row.original;
        const fullAddress = [street, city, state, country].filter(Boolean).join(', ');
        return (
          <div className="d-flex align-items-center gap-1">
            <span>{street || '-'}</span>
            {fullAddress && (
              <OverlayTrigger placement="top" overlay={<Tooltip id={`tooltip-${id}`}>{fullAddress}</Tooltip>}>
                <span className="address-info-icon" style={{ cursor: 'pointer', color: '#007bff' }}>
                  <FiInfo size={14} />
                </span>
              </OverlayTrigger>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'company',
      header: 'Company',
      cell: ({ row }) => row.original.company || '-',
    },
    {
      id: 'actions',
      header: 'Actions',
      enableSorting: false,
      cell: ({ row }) => (
        <div className="d-flex align-items-center">
          {/* <RBButton variant="link" className="text-primary p-0 me-2" size="sm">
            <FiPhone size={18} />
          </RBButton> */}
          <Dropdown>
            <Dropdown.Toggle variant="link" className="p-0">
              <FiMoreVertical size={18} />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => handleEditClick(row.original)}>
                <FiEdit className="me-2" /> Edit
              </Dropdown.Item>
              <Dropdown.Item onClick={() => handleDeleteClick(row.original.id)} className="text-danger">
                <FiTrash2 className="me-2" /> Delete
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <FillPage>
      <PageHeader
        title="Contact Group Members"
        description="Members of this contact group — search, add, edit, or remove."
        actions={
          <Button variant="outline" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        }
      />
      <div className="group-detail-container">
      <Row>
        <Col sm="12">
          <Card className="border-0 shadow-sm">
            <Card.Body className="p-0">
              <div className="px-4 py-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <Form.Control
                    type="text"
                    placeholder="Search contacts..."
                    style={{ width: '300px' }}
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                  />
                  <div className="d-flex" style={{ gap: '8px' }}>
                    {contacts.length > 0 && (
                      <Button size="sm" onClick={handleExport} disabled={buttonDisable}>
                        Export
                        {buttonDisable && (
                          <span className="spinner-border spinner-border-sm ml-2" role="status" aria-hidden="true" />
                        )}
                      </Button>
                    )}
                    <Dropdown>
                      <Dropdown.Toggle variant="primary" id="dropdown-add-contact">
                        Add Contact
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        <Dropdown.Item
                          onClick={() =>
                            navigate(`/contact-group/${id}/create-contact`, { state: { group } })
                          }
                        >
                          Create Contact
                        </Dropdown.Item>
                        {canImportContactGroupMembers() && (
                          <>
                            <Dropdown.Item onClick={handleShowUploadModal}>Upload Contact</Dropdown.Item>
                            <Dropdown.Item
                              onClick={() =>
                                navigate(`/contact-group/${id}/import-members`, { state: { group } })
                              }
                            >
                              Import Contact Members
                            </Dropdown.Item>
                          </>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>

                    <Modal show={showUploadModal} onHide={handleCloseUploadModal}>
                      <Modal.Header closeButton>
                        <Modal.Title>Upload Contacts</Modal.Title>
                      </Modal.Header>
                      <Modal.Body>
                        <p>Download the sample template and upload in same way.</p>
                        <Button
                          className="mb-4"
                          onClick={(event) => {
                            const { columns, filename, sample_data } = getColumnsAndFilename();
                            downloadTemplate(event, columns, filename, null, sample_data);
                          }}
                        >
                          Download Template
                        </Button>

                        <Form.Control
                          type="file"
                          className="form-control"
                          onChange={(e) => changeUploadFile(e)}
                          accept=".csv"
                          required
                        />
                      </Modal.Body>
                      <Modal.Footer>
                        <Button variant="outline" onClick={handleCloseUploadModal}>
                          Cancel
                        </Button>
                        <Button onClick={handleSaveUpload}>Save</Button>
                      </Modal.Footer>
                    </Modal>
                  </div>
                </div>

                <DataTable
                  tableId="contact-group.detail"
                  columns={columns}
                  data={filteredContacts}
                  emptyTitle={searchValue ? 'No contacts match your search' : 'No contacts in this group yet'}
                  pageSize={10}
                  disableExport
                />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showEditModal} onHide={handleModalClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Edit Contact</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentContact && (
            <Form
              style={{
                maxHeight: 'calc((100vh / var(--app-zoom, 1)) - 250px)',
                overflowY: 'auto',
                overflowX: 'hidden',
              }}
            >
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      First Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      name="first_name"
                      value={currentContact.first_name}
                      onChange={handleInputChange}
                      onBlur={(e) => {
                        handleInputChange({ target: { name: e.target.name, value: e.target.value.trim() } });
                      }}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Last Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="last_name"
                      value={currentContact.last_name}
                      onChange={handleInputChange}
                      onBlur={(e) => {
                        handleInputChange({ target: { name: e.target.name, value: e.target.value.trim() } });
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>
                  Primary Number <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="tel"
                  name="primary_number"
                  value={currentContact.primary_number}
                  onChange={(e) => {
                    const cleanedValue = e.target.value.replace(/\D/g, '').slice(0, 10);
                    handleInputChange({ target: { name: 'primary_number', value: cleanedValue } });
                  }}
                  maxLength={10}
                  pattern="[0-9]{10}"
                  onBlur={(e) => {
                    handleInputChange({ target: { name: e.target.name, value: e.target.value.trim() } });
                  }}
                  required
                />
                <Form.Text className="text-muted">
                  {currentContact.primary_number.length === 10
                    ? '✓ Valid 10-digit number'
                    : 'Enter 10 digits (numbers only)'}
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Alternate Number 1 (Optional)</Form.Label>
                <Form.Control
                  type="tel"
                  name="alt1_number"
                  value={currentContact.alt1_number}
                  onChange={(e) => {
                    const cleanedValue = e.target.value.replace(/\D/g, '').slice(0, 10);
                    handleInputChange({ target: { name: 'alt1_number', value: cleanedValue } });
                  }}
                  maxLength={10}
                  pattern="[0-9]{10}"
                  onBlur={(e) => {
                    handleInputChange({ target: { name: e.target.name, value: e.target.value.trim() } });
                  }}
                />
                <Form.Text className="text-muted">
                  {currentContact?.alt1_number?.length === 10
                    ? '✓ Valid 10-digit number'
                    : 'Enter 10 digits (numbers only)'}
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Alternate Number 2 (Optional)</Form.Label>
                <Form.Control
                  type="tel"
                  name="alt2_number"
                  value={currentContact.alt2_number}
                  onChange={(e) => {
                    const cleanedValue = e.target.value.replace(/\D/g, '').slice(0, 10);
                    handleInputChange({ target: { name: 'alt2_number', value: cleanedValue } });
                  }}
                  maxLength={10}
                  pattern="[0-9]{10}"
                  onBlur={(e) => {
                    handleInputChange({ target: { name: e.target.name, value: e.target.value.trim() } });
                  }}
                />
                <Form.Text className="text-muted">
                  {currentContact?.alt2_number?.length === 10
                    ? '✓ Valid 10-digit number'
                    : 'Enter 10 digits (numbers only)'}
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Street Address</Form.Label>
                <Form.Control
                  type="text"
                  name="street"
                  value={currentContact.street}
                  onChange={handleInputChange}
                  onBlur={(e) => {
                    handleInputChange({ target: { name: e.target.name, value: e.target.value.trim() } });
                  }}
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>City</Form.Label>
                    <Form.Control
                      type="text"
                      name="city"
                      value={currentContact.city}
                      onChange={handleInputChange}
                      onBlur={(e) => {
                        handleInputChange({ target: { name: e.target.name, value: e.target.value.trim() } });
                      }}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>State/Province</Form.Label>
                    <Form.Control
                      type="text"
                      name="state"
                      value={currentContact.state}
                      onChange={handleInputChange}
                      onBlur={(e) => {
                        handleInputChange({ target: { name: e.target.name, value: e.target.value.trim() } });
                      }}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Country</Form.Label>
                <Form.Control
                  type="text"
                  name="country"
                  value={currentContact.country}
                  onChange={handleInputChange}
                  onBlur={(e) => {
                    handleInputChange({ target: { name: e.target.name, value: e.target.value.trim() } });
                  }}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Company</Form.Label>
                <Form.Control
                  type="text"
                  name="company"
                  value={currentContact.company}
                  onChange={handleInputChange}
                  onBlur={(e) => {
                    handleInputChange({ target: { name: e.target.name, value: e.target.value.trim() } });
                  }}
                />
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline" onClick={handleModalClose}>
            Cancel
          </Button>
          <Button onClick={handleSaveChanges}>Save Changes</Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This removes the contact from this group. If the same contact is in other groups, those groups keep it.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline" onClick={() => setShowDeleteModal(false)}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={confirmDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
      </div>
    </FillPage>
  );
};

export default GroupDetail;
